import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface UserInstitution {
  id: string;
  name: string;
  logo_url: string | null;
  slug: string | null;
}

export function useUserInstitution(userId: string | null | undefined) {
  return useQuery({
    queryKey: ["user-institution", userId],
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<UserInstitution | null> => {
      if (!userId) return null;

      // Find an active membership for this user
      const { data: member } = await supabase
        .from("institution_members")
        .select("institution_id, status")
        .eq("user_id", userId)
        .in("status", ["active", "invited"])
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      let institutionId = member?.institution_id as string | undefined;

      // Secondary staff contacts are linked through institution_contacts.
      if (!institutionId) {
        const { data: contact } = await supabase
          .from("institution_contacts")
          .select("institution_id")
          .eq("user_id", userId)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        institutionId = contact?.institution_id as string | undefined;
      }

      // Keep support for the legacy/main-contact field.
      if (!institutionId) {
        const { data: staff } = await supabase
          .from("institutions")
          .select("id,name,logo_url,slug")
          .eq("contact_user_id", userId)
          .maybeSingle();
        if (staff) return staff as UserInstitution;
        return null;
      }

      const { data: inst } = await supabase
        .from("institutions")
        .select("id,name,logo_url,slug")
        .eq("id", institutionId)
        .maybeSingle();

      return (inst as UserInstitution) ?? null;
    },
  });
}
